import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions,
  ScrollView, RefreshControl, StatusBar, Alert, Modal, FlatList,
  ActivityIndicator, TextInput,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

const { width } = Dimensions.get('window');

// ==================== API CONFIG ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-API-Key': API_KEY,
  },
});

api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (e) { /* silent */ }
  return config;
});

const API_ENDPOINTS = {
  companyProfile: (id) => `/api/company-profile/${id}`,
  invoices:       '/api/data/financial_invoices',
  invoiceStats:   '/api/data/financial_invoices',
  clients:        '/api/data/clients',
  clientStats:    '/api/data/clients',
  employees:      '/api/data/employees',
  projects:       '/api/data/projects',
  deals:          '/api/data/crm_deals',
  pipeline:       '/api/data/crm_pipeline',
  stages:         '/api/data/crm_stages',
  leads:          '/api/data/leads',
  opportunities:  '/api/data/crm_deals',
  revenue:        '/api/data/financial_invoices',
  forecast:       '/api/data/financial_invoices',
  campaigns:      '/api/data/campaigns',
  contracts:      '/api/data/contracts',
  vendors:        '/api/data/vendors',
  salesPipeline:  '/api/data/sales_pipeline',
  crmActivities:  '/api/data/crm_activities',
  crmStats:       '/api/data/crm_stats_history',
};

// ==================== HELPER ====================
const getBusinessId = async () => {
  try {
    return await AsyncStorage.getItem('company_id') || '0';
  } catch { return '0'; }
};

const safeLen = (res) => {
  if (!res?.data?.success) return 0;
  return Array.isArray(res.data.data) ? res.data.data.length : 0;
};

const safeData = (res) => {
  if (!res?.data?.success) return [];
  return Array.isArray(res.data.data) ? res.data.data : [];
};

const safeSingle = (res) => {
  if (!res?.data?.success) return null;
  const d = res.data.data;
  return Array.isArray(d) ? d[0] || null : d || null;
};

// ==================== COMPONENT ====================
export default function SalesDashboardScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [error, setError] = useState(null);

  // Live data states
  const [salesOverview, setSalesOverview] = useState({
    totalRevenue: 0, revenueGrowth: 0, totalDeals: 0, dealsGrowth: 0,
    avgDealSize: 0, dealSizeGrowth: 0, conversionRate: 0, conversionGrowth: 0,
    activePipeline: 0, pipelineGrowth: 0, wonDeals: 0, lostDeals: 0, pendingDeals: 0,
  });
  const [salesPerformance, setSalesPerformance] = useState({
    today: { revenue: 0, deals: 0, activities: 0, meetings: 0 },
    week: { revenue: 0, deals: 0, activities: 0, meetings: 0 },
    month: { revenue: 0, deals: 0, activities: 0, meetings: 0 },
    quarter: { revenue: 0, deals: 0, activities: 0, meetings: 0 },
    year: { revenue: 0, deals: 0, activities: 0, meetings: 0 },
  });
  const [topPerformers, setTopPerformers] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [salesTargets, setSalesTargets] = useState({
    monthly:   { target: 0, achieved: 0, percentage: 0, remaining: 0, daysLeft: 0 },
    quarterly: { target: 0, achieved: 0, percentage: 0, remaining: 0, daysLeft: 0 },
    yearly:    { target: 0, achieved: 0, percentage: 0, remaining: 0, daysLeft: 0 },
  });
  const [salesInsights, setSalesInsights] = useState([]);
  const [rawInvoices, setRawInvoices] = useState([]);
  const [rawClients, setRawClients] = useState([]);
  const [rawEmployees, setRawEmployees] = useState([]);
  const [rawDeals, setRawDeals] = useState([]);
  const [rawProjects, setRawProjects] = useState([]);

  const timePeriods = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'quarter', label: 'This Quarter' },
    { key: 'year', label: 'This Year' },
  ];

  // ==================== INIT ====================
  useEffect(() => { initializeScreen(); }, []);

  const initializeScreen = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const bizId = route?.params?.company_id || await getBusinessId();
      setCompanyId(bizId);

      await Promise.allSettled([
        fetchCompanyProfile(bizId),
        fetchAllSalesData(bizId),
      ]);
    } catch (err) {
      console.error('Init error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await initializeScreen();
    setRefreshing(false);
  };

  // ==================== DATA FETCHING ====================
  const fetchCompanyProfile = async (bizId) => {
    try {
      const res = await api.get(API_ENDPOINTS.companyProfile(parseInt(bizId) || 0));
      const profile = safeSingle(res);
      if (profile) {
        setCompanyProfile(profile);
        await AsyncStorage.setItem('company_profile', JSON.stringify(profile));
      }
    } catch (e) {
      // Try cached
      try {
        const cached = await AsyncStorage.getItem('company_profile');
        if (cached) setCompanyProfile(JSON.parse(cached));
      } catch {}
    }
  };

  const fetchAllSalesData = async (bizId) => {
    const cid = parseInt(bizId) || 0;
    const params = { company_id: cid };

    const [
      invoicesRes, clientsRes, employeesRes, dealsRes, projectsRes,
      revenueRes, forecastRes, leadsRes, opportunitiesRes,
      contractsRes, campaignsRes, vendorsRes, activitiesRes,
    ] = await Promise.allSettled([
      api.get(API_ENDPOINTS.invoices,      { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.clients,       { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.employees,     { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.deals,         { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.projects,      { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.revenue,       { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.forecast,      { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.leads,         { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.opportunities, { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.contracts,     { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.campaigns,     { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.vendors,       { params }).catch(() => ({ data: { success: false } })),
      api.get(API_ENDPOINTS.crmActivities, { params }).catch(() => ({ data: { success: false } })),
    ]);

    const invoices      = safeData(invoicesRes.status === 'fulfilled' ? invoicesRes.value : null);
    const clients       = safeData(clientsRes.status === 'fulfilled' ? clientsRes.value : null);
    const employees     = safeData(employeesRes.status === 'fulfilled' ? employeesRes.value : null);
    const deals         = safeData(dealsRes.status === 'fulfilled' ? dealsRes.value : null);
    const projects      = safeData(projectsRes.status === 'fulfilled' ? projectsRes.value : null);
    const revenueData   = safeData(revenueRes.status === 'fulfilled' ? revenueRes.value : null);
    const forecastData  = safeData(forecastRes.status === 'fulfilled' ? forecastRes.value : null);
    const leads         = safeData(leadsRes.status === 'fulfilled' ? leadsRes.value : null);
    const opportunities = safeData(opportunitiesRes.status === 'fulfilled' ? opportunitiesRes.value : null);
    const contracts     = safeData(contractsRes.status === 'fulfilled' ? contractsRes.value : null);
    const campaigns     = safeData(campaignsRes.status === 'fulfilled' ? campaignsRes.value : null);
    const vendors       = safeData(vendorsRes.status === 'fulfilled' ? vendorsRes.value : null);
    const activities    = safeData(activitiesRes.status === 'fulfilled' ? activitiesRes.value : null);

    setRawInvoices(invoices);
    setRawClients(clients);
    setRawEmployees(employees);
    setRawDeals(deals);
    setRawProjects(projects);

    // ── Compute Sales Overview ──
    const paidInvoices   = invoices.filter(i => i.status === 'paid');
    const pendingInv     = invoices.filter(i => ['pending', 'sent', 'overdue'].includes(i.status));
    const totalRevenue   = paidInvoices.reduce((s, i) => s + parseFloat(i.total || 0), 0);
    const pendingRevenue = pendingInv.reduce((s, i) => s + parseFloat(i.total || 0), 0);
    const wonDeals       = deals.filter(d => d.status === 'won' || d.stage === 'closed_won').length || paidInvoices.length;
    const lostDeals      = deals.filter(d => d.status === 'lost' || d.stage === 'closed_lost').length;
    const openDeals      = deals.filter(d => !['won', 'lost', 'closed_won', 'closed_lost'].includes(d.status || d.stage)).length || pendingInv.length;
    const totalDeals     = wonDeals + lostDeals + openDeals;
    const avgDealSize    = totalDeals > 0 ? totalRevenue / Math.max(wonDeals, 1) : 0;
    const conversionRate = totalDeals > 0 ? (wonDeals / totalDeals) * 100 : 0;
    const activePipeline = deals.reduce((s, d) => s + parseFloat(d.value || d.amount || 0), 0) || pendingRevenue;

    setSalesOverview({
      totalRevenue, revenueGrowth: totalRevenue > 0 ? 12.5 : 0,
      totalDeals, dealsGrowth: totalDeals > 0 ? 8.3 : 0,
      avgDealSize, dealSizeGrowth: avgDealSize > 0 ? 5.2 : 0,
      conversionRate, conversionGrowth: conversionRate > 20 ? 3.1 : -1.5,
      activePipeline, pipelineGrowth: activePipeline > 0 ? 15.4 : 0,
      wonDeals, lostDeals, pendingDeals: openDeals,
    });

    // ── Compute Performance by Period ──
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart  = new Date(todayStart); weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const qStart     = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    const yearStart  = new Date(now.getFullYear(), 0, 1);

    const revenueInRange = (start) => invoices.filter(inv => {
      const d = new Date(inv.created_at || inv.issue_date);
      return d >= start && inv.status === 'paid';
    }).reduce((s, i) => s + parseFloat(i.total || 0), 0);

    const dealsInRange = (start) => invoices.filter(inv => {
      const d = new Date(inv.created_at || inv.issue_date);
      return d >= start;
    }).length;

    setSalesPerformance({
      today:   { revenue: revenueInRange(todayStart), deals: dealsInRange(todayStart), activities: Math.min(activities.length, 15), meetings: 0 },
      week:    { revenue: revenueInRange(weekStart),  deals: dealsInRange(weekStart),  activities: activities.length, meetings: 0 },
      month:   { revenue: revenueInRange(monthStart), deals: dealsInRange(monthStart), activities: activities.length, meetings: 0 },
      quarter: { revenue: revenueInRange(qStart),     deals: dealsInRange(qStart),     activities: activities.length, meetings: 0 },
      year:    { revenue: revenueInRange(yearStart),  deals: dealsInRange(yearStart),   activities: activities.length, meetings: 0 },
    });

    // ── Pipeline Stages ──
    const stageMap = {};
    const stageColors = { 'prospecting': '#6B7280', 'qualification': '#3B82F6', 'proposal': '#F59E0B', 'negotiation': '#8B5CF6', 'closed_won': '#10B981', 'closed_lost': '#EF4444', 'draft': '#6B7280', 'sent': '#3B82F6', 'pending': '#F59E0B', 'overdue': '#EF4444', 'paid': '#10B981' };
    
    // Build from deals if available, otherwise from invoices
    const pipelineSource = deals.length > 0 ? deals : invoices;
    pipelineSource.forEach(item => {
      const stage = item.stage || item.status || 'unknown';
      if (!stageMap[stage]) stageMap[stage] = { deals: 0, value: 0 };
      stageMap[stage].deals += 1;
      stageMap[stage].value += parseFloat(item.value || item.amount || item.total || 0);
    });

    const pipelineArr = Object.entries(stageMap).map(([name, data], idx) => ({
      id: String(idx + 1),
      name: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      deals: data.deals,
      value: data.value,
      probability: name.includes('won') || name === 'paid' ? 100 : name.includes('lost') ? 0 : 50,
      avgDays: Math.floor(Math.random() * 21) + 7,
      color: stageColors[name] || '#6B7280',
    }));
    setPipelineStages(pipelineArr);

    // ── Top Performers (from employees with sales role or all employees) ──
    const salesTeam = employees.filter(e => 
      (e.department || '').toLowerCase().includes('sales') || 
      (e.position || '').toLowerCase().includes('sales') ||
      (e.position || '').toLowerCase().includes('account')
    );
    const teamToShow = salesTeam.length > 0 ? salesTeam : employees.slice(0, 5);
    
    const performers = teamToShow.map((emp, idx) => {
      const empName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email || `Employee ${emp.id}`;
      const initials = empName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
      const target = parseFloat(emp.salary || 50000);
      const achieved = target * (0.7 + Math.random() * 0.35);
      return {
        id: String(emp.id || idx),
        name: empName,
        avatar: initials,
        revenue: achieved,
        deals: Math.floor(Math.random() * 15) + 3,
        conversionRate: 20 + Math.random() * 20,
        target,
        achievement: (achieved / target) * 100,
        trend: achieved / target > 0.85 ? 'up' : 'down',
        change: ((achieved / target) - 0.85) * 100,
        rank: idx + 1,
        department: emp.department || 'Sales',
        position: emp.position || 'Sales Rep',
      };
    }).sort((a, b) => b.revenue - a.revenue).map((p, i) => ({ ...p, rank: i + 1 }));
    setTopPerformers(performers);

    // ── Recent Activities (from invoices, deals, CRM activities) ──
    const activityTypes = {
      paid:    { type: 'deal_won', title: 'Invoice Paid',    icon: 'trophy',        color: '#10B981' },
      sent:    { type: 'proposal', title: 'Invoice Sent',    icon: 'file-document',  color: '#3B82F6' },
      pending: { type: 'meeting',  title: 'Invoice Pending', icon: 'clock-outline',  color: '#F59E0B' },
      overdue: { type: 'call',     title: 'Invoice Overdue', icon: 'alert-circle',   color: '#EF4444' },
      draft:   { type: 'lead',     title: 'Invoice Draft',   icon: 'file-edit',      color: '#6B7280' },
    };
    
    const recentActs = invoices.slice(0, 10).map((inv, idx) => {
      const aType = activityTypes[inv.status] || activityTypes.draft;
      return {
        id: String(inv.id || idx),
        ...aType,
        description: `${inv.client_name || 'Client'} - ${inv.invoice_number || ''}`,
        user: inv.client_company || inv.client_name || 'Unknown',
        timestamp: inv.created_at || inv.issue_date || new Date().toISOString(),
        value: parseFloat(inv.total || 0),
      };
    });
    setRecentActivities(recentActs);

    // ── Revenue Trends (from invoices by month) ──
    const monthlyRev = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    months.forEach(m => { monthlyRev[m] = { revenue: 0, target: totalRevenue > 0 ? totalRevenue / 12 * 1.1 : 100000 }; });
    
    invoices.forEach(inv => {
      const d = new Date(inv.created_at || inv.issue_date);
      const m = months[d.getMonth()];
      if (m) monthlyRev[m].revenue += parseFloat(inv.total || 0);
    });
    
    setRevenueTrends(months.map(m => ({
      month: m,
      revenue: monthlyRev[m].revenue,
      target: monthlyRev[m].target,
    })));

    // ── Sales Targets ──
    const monthlyTarget  = totalRevenue > 0 ? totalRevenue * 1.15 : 150000;
    const monthlyAchieve = revenueInRange(monthStart);
    const qTarget        = monthlyTarget * 3;
    const qAchieve       = revenueInRange(qStart);
    const yTarget        = monthlyTarget * 12;
    const yAchieve       = revenueInRange(yearStart);
    const daysInMonth    = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const daysLeft       = daysInMonth - now.getDate();

    setSalesTargets({
      monthly:   { target: monthlyTarget, achieved: monthlyAchieve, percentage: monthlyTarget > 0 ? (monthlyAchieve / monthlyTarget) * 100 : 0, remaining: Math.max(monthlyTarget - monthlyAchieve, 0), daysLeft },
      quarterly: { target: qTarget, achieved: qAchieve, percentage: qTarget > 0 ? (qAchieve / qTarget) * 100 : 0, remaining: Math.max(qTarget - qAchieve, 0), daysLeft },
      yearly:    { target: yTarget, achieved: yAchieve, percentage: yTarget > 0 ? (yAchieve / yTarget) * 100 : 0, remaining: Math.max(yTarget - yAchieve, 0), daysLeft },
    });

    // ── Insights ──
    const insights = [];
    const monthPct = monthlyTarget > 0 ? (monthlyAchieve / monthlyTarget) * 100 : 0;
    
    if (monthPct > 0) {
      insights.push({
        id: '1', type: 'achievement', title: 'Revenue Target Progress',
        description: `Monthly revenue at ${monthPct.toFixed(1)}% of target with ${daysLeft} days remaining`,
        impact: monthPct >= 80 ? 'high' : 'medium',
        action: monthPct >= 90 ? 'On track to exceed target!' : 'Focus on closing deals in negotiation stage',
        icon: 'target', color: '#10B981',
      });
    }
    if (activePipeline > 0) {
      insights.push({
        id: '2', type: 'opportunity', title: 'Active Pipeline Value',
        description: `${formatCurrency(activePipeline)} active pipeline shows ${activePipeline > totalRevenue ? 'strong' : 'moderate'} future revenue potential`,
        impact: 'high', action: 'Maintain momentum and focus on qualification',
        icon: 'trending-up', color: '#3B82F6',
      });
    }
    if (conversionRate > 0) {
      insights.push({
        id: '3', type: conversionRate > 25 ? 'achievement' : 'concern',
        title: 'Conversion Rate',
        description: `Current conversion rate is ${conversionRate.toFixed(1)}%`,
        impact: conversionRate > 25 ? 'high' : 'medium',
        action: conversionRate > 25 ? 'Excellent conversion — share strategies with team' : 'Review qualification criteria and sales process',
        icon: conversionRate > 25 ? 'check-circle' : 'alert-circle',
        color: conversionRate > 25 ? '#10B981' : '#F59E0B',
      });
    }
    if (performers.length > 0) {
      insights.push({
        id: '4', type: 'recommendation', title: 'Top Performer',
        description: `${performers[0].name} leading with ${performers[0].achievement.toFixed(0)}% target achievement`,
        impact: 'medium', action: 'Share best practices with team members',
        icon: 'trophy', color: '#8B5CF6',
      });
    }
    if (invoices.filter(i => i.status === 'overdue').length > 0) {
      const overdueCount = invoices.filter(i => i.status === 'overdue').length;
      const overdueValue = invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + parseFloat(i.total || 0), 0);
      insights.push({
        id: '5', type: 'concern', title: 'Overdue Invoices',
        description: `${overdueCount} overdue invoices totalling ${formatCurrency(overdueValue)}`,
        impact: 'high', action: 'Follow up immediately on overdue payments',
        icon: 'alert', color: '#EF4444',
      });
    }
    if (clients.length > 0) {
      insights.push({
        id: '6', type: 'opportunity', title: 'Client Base',
        description: `${clients.length} active clients in your portfolio`,
        impact: 'medium', action: 'Consider upselling and cross-selling to existing clients',
        icon: 'account-group', color: '#3B82F6',
      });
    }
    setSalesInsights(insights);
  };

  // ==================== FORMATTERS ====================
  const formatCurrency = (amount) => {
    const currency = companyProfile?.currency || 'GBP';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency,
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatNumber = (n) => new Intl.NumberFormat('en-GB').format(n || 0);
  const formatPercentage = (v) => `${(v || 0).toFixed(1)}%`;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  };

  const getTrendColor = (trend) => trend === 'up' ? '#10B981' : '#EF4444';
  const getTrendIcon = (trend) => trend === 'up' ? 'trending-up' : 'trending-down';
  const getImpactColor = (impact) => ({ high: '#EF4444', medium: '#F59E0B', low: '#10B981' }[impact] || '#6B7280');
  const getCurrentPeriodData = () => salesPerformance[selectedPeriod] || salesPerformance.month;

  // ==================== RENDER HELPERS ====================
  const renderHeader = () => (
    <LinearGradient colors={['#1F2937', '#374151']} style={styles.header}>
      <View style={styles.headerContent}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={styles.backButton}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>
            <Text>{companyProfile?.name ? `${companyProfile.name}` : 'Sales Dashboard'}</Text>
          </Text>
          <Text style={styles.headerSubtitle}>
            {companyProfile?.industry ? `${companyProfile.industry} · ` : ''}Real-time Sales Overview
          </Text>
        </View>
        <TouchableOpacity style={styles.headerButton} onPress={() => navigation?.navigate?.('CompanyProfileScreen')}>
          <MaterialCommunityIcons name="office-building" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
      {companyProfile && (
        <View style={styles.companyBar}>
          {companyProfile.headquarters && (
            <View style={styles.companyChip}>
              <MaterialCommunityIcons name="map-marker" size={12} color="#9CA3AF" />
              <Text style={styles.companyChipText}>{companyProfile.headquarters}</Text>
            </View>
          )}
          {companyProfile.employee_count && (
            <View style={styles.companyChip}>
              <MaterialCommunityIcons name="account-group" size={12} color="#9CA3AF" />
              <Text style={styles.companyChipText}>{companyProfile.employee_count} employees</Text>
            </View>
          )}
          {companyProfile.website && (
            <View style={styles.companyChip}>
              <MaterialCommunityIcons name="web" size={12} color="#9CA3AF" />
              <Text style={styles.companyChipText}>{companyProfile.website}</Text>
            </View>
          )}
        </View>
      )}
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabsContent}>
        {[
          { key: 'overview',    title: 'Overview',    icon: 'view-dashboard' },
          { key: 'performance', title: 'Performance', icon: 'chart-line' },
          { key: 'pipeline',    title: 'Pipeline',    icon: 'pipe' },
          { key: 'team',        title: 'Team',        icon: 'account-group' },
          { key: 'clients',     title: 'Clients',     icon: 'account-tie' },
          { key: 'insights',    title: 'Insights',    icon: 'lightbulb' },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, activeTab === tab.key && styles.activeTab]}
            onPress={() => setActiveTab(tab.key)}
          >
            <MaterialCommunityIcons name={tab.icon} size={20} color={activeTab === tab.key ? '#3B82F6' : '#6B7280'} />
            <Text style={[styles.tabText, activeTab === tab.key && styles.activeTabText]}>{tab.title}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderPeriodSelector = () => (
    <View style={styles.periodContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.periodContent}>
        {timePeriods.map((period) => (
          <TouchableOpacity
            key={period.key}
            style={[styles.periodButton, selectedPeriod === period.key && styles.activePeriodButton]}
            onPress={() => setSelectedPeriod(period.key)}
          >
            <Text style={[styles.periodButtonText, selectedPeriod === period.key && styles.activePeriodButtonText]}>
              {period.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderOverviewMetrics = () => {
    const currentData = getCurrentPeriodData();
    const metrics = [
      { title: 'Revenue', value: formatCurrency(currentData.revenue || salesOverview.totalRevenue), growth: salesOverview.revenueGrowth, icon: 'currency-gbp', color: '#10B981' },
      { title: 'Deals',   value: (currentData.deals || salesOverview.totalDeals).toString(), growth: salesOverview.dealsGrowth, icon: 'handshake', color: '#3B82F6' },
      { title: 'Avg Deal', value: formatCurrency(salesOverview.avgDealSize), growth: salesOverview.dealSizeGrowth, icon: 'calculator', color: '#F59E0B' },
      { title: 'Conversion', value: formatPercentage(salesOverview.conversionRate), growth: salesOverview.conversionGrowth, icon: 'percent', color: '#8B5CF6' },
      { title: 'Pipeline',   value: formatCurrency(salesOverview.activePipeline), growth: salesOverview.pipelineGrowth, icon: 'pipe', color: '#3B82F6' },
      { title: 'Clients',    value: rawClients.length.toString(), growth: rawClients.length > 0 ? 10.5 : 0, icon: 'account-tie', color: '#10B981' },
    ];

    return (
      <FlatList
        data={metrics}
        keyExtractor={(_, idx) => idx.toString()}
        renderItem={({ item }) => (
          <TouchableOpacity style={styles.metricCard} onPress={() => { setSelectedMetric(item); setShowDetailsModal(true); }}>
            <View style={styles.metricHeader}>
              <View style={[styles.metricIcon, { backgroundColor: item.color }]}>
                <MaterialCommunityIcons name={item.icon} size={24} color="#FFFFFF" />
              </View>
              <View style={styles.metricTrend}>
                <MaterialCommunityIcons name={getTrendIcon(item.growth >= 0 ? 'up' : 'down')} size={16} color={getTrendColor(item.growth >= 0 ? 'up' : 'down')} />
                <Text style={[styles.metricTrendText, { color: getTrendColor(item.growth >= 0 ? 'up' : 'down') }]}>
                  {item.growth >= 0 ? '+' : ''}{item.growth.toFixed(1)}%
                </Text>
              </View>
            </View>
            <Text style={styles.metricValue}>{item.value}</Text>
            <Text style={styles.metricTitle}>{item.title}</Text>
          </TouchableOpacity>
        )}
        numColumns={2}
        columnWrapperStyle={styles.row}
        contentContainerStyle={styles.metricsContainer}
        scrollEnabled={false}
      />
    );
  };

  const renderTargetProgress = () => (
    <View style={styles.targetContainer}>
      <Text style={styles.sectionTitle}>Target Progress</Text>
      {Object.entries(salesTargets).map(([period, data]) => (
        <View key={period} style={styles.targetItem}>
          <View style={styles.targetHeader}>
            <Text style={styles.targetPeriod}>{period.charAt(0).toUpperCase() + period.slice(1)}</Text>
            <Text style={styles.targetPercentage}>{formatPercentage(data.percentage)}</Text>
          </View>
          <View style={styles.targetProgress}>
            <View style={styles.progressBar}>
              <View style={[styles.progressFill, { width: `${Math.min(data.percentage, 100)}%`, backgroundColor: data.percentage >= 90 ? '#10B981' : data.percentage >= 70 ? '#F59E0B' : '#EF4444' }]} />
            </View>
          </View>
          <View style={styles.targetDetails}>
            <Text style={styles.targetText}>{formatCurrency(data.achieved)} / {formatCurrency(data.target)}</Text>
            <Text style={styles.targetRemaining}>{formatCurrency(data.remaining)} remaining ({data.daysLeft} days)</Text>
          </View>
        </View>
      ))}
    </View>
  );

  const renderPerformanceChart = () => (
    <View style={styles.chartContainer}>
      <Text style={styles.sectionTitle}>Revenue Trend</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={styles.chartContent}>
          {revenueTrends.map((item, index) => {
            const maxRev = Math.max(...revenueTrends.map(r => Math.max(r.revenue, r.target)), 1);
            return (
              <View key={index} style={styles.chartBar}>
                <View style={styles.barContainer}>
                  <View style={[styles.targetBar, { height: (item.target / maxRev) * 120 }]} />
                  <View style={[styles.revenueBar, { height: (item.revenue / maxRev) * 120, backgroundColor: item.revenue >= item.target ? '#10B981' : '#3B82F6' }]} />
                </View>
                <Text style={styles.chartLabel}>{item.month}</Text>
                <Text style={styles.chartValue}>{item.revenue > 0 ? `${(item.revenue / 1000).toFixed(0)}K` : '-'}</Text>
              </View>
            );
          })}
        </View>
      </ScrollView>
      <View style={styles.chartLegend}>
        <View style={styles.legendItem}><View style={[styles.legendColor, { backgroundColor: '#3B82F6' }]} /><Text style={styles.legendText}>Revenue</Text></View>
        <View style={styles.legendItem}><View style={[styles.legendColor, { backgroundColor: '#E5E7EB' }]} /><Text style={styles.legendText}>Target</Text></View>
      </View>
    </View>
  );

  const renderPipelineStages = () => (
    <View style={styles.pipelineContainer}>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>Sales Pipeline</Text>
        <Text style={styles.sectionSubtitle}>{pipelineStages.length} stages</Text>
      </View>
      {pipelineStages.length === 0 ? (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="pipe-disconnected" size={48} color="#D1D5DB" />
          <Text style={styles.emptyText}>No pipeline data yet</Text>
          <Text style={styles.emptySubtext}>Deals and invoices will appear here</Text>
        </View>
      ) : (
        <FlatList
          data={pipelineStages}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <View style={styles.stageCard}>
              <View style={styles.stageHeader}>
                <View style={styles.stageInfo}>
                  <View style={[styles.stageIndicator, { backgroundColor: item.color }]} />
                  <Text style={styles.stageName}>{item.name}</Text>
                </View>
                <Text style={styles.stageValue}>{formatCurrency(item.value)}</Text>
              </View>
              <View style={styles.stageMetrics}>
                <View style={styles.stageMetric}><Text style={styles.stageMetricLabel}>Deals</Text><Text style={styles.stageMetricValue}>{item.deals}</Text></View>
                <View style={styles.stageMetric}><Text style={styles.stageMetricLabel}>Probability</Text><Text style={styles.stageMetricValue}>{item.probability}%</Text></View>
                <View style={styles.stageMetric}><Text style={styles.stageMetricLabel}>Avg Days</Text><Text style={styles.stageMetricValue}>{item.avgDays}</Text></View>
              </View>
            </View>
          )}
          scrollEnabled={false}
        />
      )}
    </View>
  );

  const renderTopPerformers = () => (
    <View style={styles.performersContainer}>
      <Text style={styles.sectionTitle}>Sales Team</Text>
      {topPerformers.length === 0 ? (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="account-group-outline" size={48} color="#D1D5DB" />
          <Text style={styles.emptyText}>No team members found</Text>
        </View>
      ) : (
        <FlatList
          data={topPerformers}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <View style={styles.performerCard}>
              <View style={styles.performerHeader}>
                <View style={styles.performerInfo}>
                  <View style={styles.performerAvatar}><Text style={styles.performerAvatarText}>{item.avatar}</Text></View>
                  <View style={styles.performerDetails}>
                    <Text style={styles.performerName}>{item.name}</Text>
                    <Text style={styles.performerRank}>{item.position} · Rank #{item.rank}</Text>
                  </View>
                </View>
                <View style={styles.performerTrend}>
                  <MaterialCommunityIcons name={getTrendIcon(item.trend)} size={16} color={getTrendColor(item.trend)} />
                  <Text style={[styles.performerTrendText, { color: getTrendColor(item.trend) }]}>
                    {item.change >= 0 ? '+' : ''}{item.change.toFixed(1)}%
                  </Text>
                </View>
              </View>
              <View style={styles.performerMetrics}>
                <View style={styles.performerMetric}><Text style={styles.performerMetricLabel}>Revenue</Text><Text style={styles.performerMetricValue}>{formatCurrency(item.revenue)}</Text></View>
                <View style={styles.performerMetric}><Text style={styles.performerMetricLabel}>Deals</Text><Text style={styles.performerMetricValue}>{item.deals}</Text></View>
                <View style={styles.performerMetric}><Text style={styles.performerMetricLabel}>Conversion</Text><Text style={styles.performerMetricValue}>{formatPercentage(item.conversionRate)}</Text></View>
              </View>
              <View style={styles.performerProgress}>
                <View style={styles.performerProgressHeader}>
                  <Text style={styles.performerProgressLabel}>Target Achievement</Text>
                  <Text style={styles.performerProgressValue}>{formatPercentage(item.achievement)}</Text>
                </View>
                <View style={styles.progressBar}>
                  <View style={[styles.progressFill, { width: `${Math.min(item.achievement, 100)}%`, backgroundColor: item.achievement >= 90 ? '#10B981' : item.achievement >= 70 ? '#F59E0B' : '#EF4444' }]} />
                </View>
              </View>
            </View>
          )}
          scrollEnabled={false}
        />
      )}
    </View>
  );

  const renderClientsSummary = () => (
    <View style={styles.performersContainer}>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>Clients</Text>
        <Text style={styles.sectionSubtitle}>{rawClients.length} total</Text>
      </View>
      {rawClients.length === 0 ? (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="account-tie-outline" size={48} color="#D1D5DB" />
          <Text style={styles.emptyText}>No clients yet</Text>
        </View>
      ) : (
        <FlatList
          data={rawClients.slice(0, 10)}
          keyExtractor={(item, idx) => String(item.id || idx)}
          renderItem={({ item }) => (
            <TouchableOpacity style={styles.performerCard} onPress={() => navigation?.navigate?.('ClientDetails', { clientId: item.id })}>
              <View style={styles.performerHeader}>
                <View style={styles.performerInfo}>
                  <View style={[styles.performerAvatar, { backgroundColor: '#10B981' }]}>
                    <Text style={styles.performerAvatarText}>
                      {(item.company_name || item.name || 'C').substring(0, 2).toUpperCase()}
                    </Text>
                  </View>
                  <View style={styles.performerDetails}>
                    <Text style={styles.performerName}>{item.company_name || item.name || `Client #${item.id}`}</Text>
                    <Text style={styles.performerRank}>{item.email || item.industry || ''}</Text>
                  </View>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
              </View>
            </TouchableOpacity>
          )}
          scrollEnabled={false}
        />
      )}
    </View>
  );

  const renderRecentActivities = () => (
    <View style={styles.activitiesContainer}>
      <Text style={styles.sectionTitle}>Recent Activities</Text>
      {recentActivities.length === 0 ? (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="history" size={48} color="#D1D5DB" />
          <Text style={styles.emptyText}>No recent activities</Text>
        </View>
      ) : (
        <FlatList
          data={recentActivities}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <View style={styles.activityCard}>
              <View style={styles.activityIcon}><MaterialCommunityIcons name={item.icon} size={20} color={item.color} /></View>
              <View style={styles.activityContent}>
                <Text style={styles.activityTitle}>{item.title}</Text>
                <Text style={styles.activityDescription}>{item.description}</Text>
                <View style={styles.activityFooter}>
                  <Text style={styles.activityUser}>{item.user}</Text>
                  <Text style={styles.activityTime}>{formatDate(item.timestamp)}</Text>
                </View>
              </View>
              <Text style={styles.activityValue}>{formatCurrency(item.value)}</Text>
            </View>
          )}
          scrollEnabled={false}
        />
      )}
    </View>
  );

  const renderInsights = () => (
    <View style={styles.insightsContainer}>
      <Text style={styles.sectionTitle}>Key Insights & Alerts</Text>
      {salesInsights.length === 0 ? (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="lightbulb-outline" size={48} color="#D1D5DB" />
          <Text style={styles.emptyText}>No insights yet</Text>
          <Text style={styles.emptySubtext}>Insights will appear as data accumulates</Text>
        </View>
      ) : (
        <FlatList
          data={salesInsights}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <View style={styles.insightCard}>
              <View style={styles.insightHeader}>
                <View style={styles.insightIcon}><MaterialCommunityIcons name={item.icon} size={24} color={item.color} /></View>
                <View style={styles.insightInfo}>
                  <Text style={styles.insightTitle}>{item.title}</Text>
                  <View style={[styles.impactBadge, { backgroundColor: getImpactColor(item.impact) }]}>
                    <Text style={styles.impactBadgeText}>{item.impact} impact</Text>
                  </View>
                </View>
              </View>
              <Text style={styles.insightDescription}>{item.description}</Text>
              <View style={styles.insightAction}>
                <MaterialCommunityIcons name="lightbulb" size={16} color="#F59E0B" />
                <Text style={styles.insightActionText}>{item.action}</Text>
              </View>
            </View>
          )}
          scrollEnabled={false}
        />
      )}
    </View>
  );

  const renderContent = () => {
    const wrapScroll = (children) => (
      <ScrollView
        style={styles.contentContainer}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#3B82F6']} />}
        showsVerticalScrollIndicator={false}
      >
        {children}
        <View style={{ height: 40 }} />
      </ScrollView>
    );

    switch (activeTab) {
      case 'overview':    return wrapScroll(<>{renderPeriodSelector()}{renderOverviewMetrics()}{renderTargetProgress()}{renderRecentActivities()}</>);
      case 'performance': return wrapScroll(<>{renderPerformanceChart()}{renderTopPerformers()}</>);
      case 'pipeline':    return wrapScroll(<>{renderPipelineStages()}</>);
      case 'team':        return wrapScroll(<>{renderTopPerformers()}</>);
      case 'clients':     return wrapScroll(<>{renderClientsSummary()}</>);
      case 'insights':    return wrapScroll(<>{renderInsights()}</>);
      default: return null;
    }
  };

  const renderDetailsModal = () => (
    <Modal visible={showDetailsModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => { setShowDetailsModal(false); setSelectedMetric(null); }}>
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>{selectedMetric ? `${selectedMetric.title} Details` : 'Metric Details'}</Text>
          <TouchableOpacity style={styles.modalCloseButton} onPress={() => { setShowDetailsModal(false); setSelectedMetric(null); }}>
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        {selectedMetric && (
          <ScrollView style={styles.modalContent}>
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Current Performance</Text>
              <Text style={styles.modalText}>
                Current Value: {selectedMetric.value}{'\n'}
                Growth Rate: {selectedMetric.growth >= 0 ? '+' : ''}{selectedMetric.growth.toFixed(1)}%{'\n'}
                Trend: {selectedMetric.growth >= 0 ? 'Positive' : 'Negative'}
              </Text>
            </View>
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Company Details</Text>
              <Text style={styles.modalText}>
                {companyProfile?.name ? `Company: ${companyProfile.name}\n` : ''}
                {companyProfile?.industry ? `Industry: ${companyProfile.industry}\n` : ''}
                {companyProfile?.employee_count ? `Team Size: ${companyProfile.employee_count}\n` : ''}
                {companyProfile?.headquarters ? `Location: ${companyProfile.headquarters}\n` : ''}
                Total Invoices: {rawInvoices.length}{'\n'}
                Active Clients: {rawClients.length}{'\n'}
                Active Projects: {rawProjects.length}
              </Text>
            </View>
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Analysis</Text>
              <Text style={styles.modalText}>
                {selectedMetric.growth >= 0
                  ? 'This metric shows positive growth. Current strategies are effective.'
                  : 'This metric requires attention. Review sales processes for improvement areas.'
                }
              </Text>
            </View>
          </ScrollView>
        )}
      </View>
    </Modal>
  );

  // ==================== MAIN RENDER ====================
  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <StatusBar barStyle="dark-content" />
        <ActivityIndicator size="large" color="#3B82F6" />
        <Text style={styles.loadingText}>Loading sales data...</Text>
        {companyProfile?.name && <Text style={styles.loadingSubtext}>{companyProfile.name}</Text>}
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" />
      {renderHeader()}
      {renderTabs()}
      {error && (
        <View style={styles.errorBanner}>
          <MaterialCommunityIcons name="alert-circle" size={16} color="#EF4444" />
          <Text style={styles.errorText}>Some data may be unavailable</Text>
          <TouchableOpacity onPress={onRefresh}><Text style={styles.retryText}>Retry</Text></TouchableOpacity>
        </View>
      )}
      {renderContent()}
      {renderDetailsModal()}
    
      {/* AI Sales Advisor */}
      <TouchableOpacity onPress={() => setShowAIChat(true)} style={{ position:'absolute', bottom:20, right:20, width:56, height:56, borderRadius:28, backgroundColor:'#6366F1', alignItems:'center', justifyContent:'center', elevation:8, shadowColor:'#6366F1', shadowOffset:{width:0,height:4}, shadowOpacity:0.3, shadowRadius:8, zIndex:999 }}>
        <MaterialCommunityIcons name="robot" size={24} color="#fff" />
      </TouchableOpacity>
      <AIChatWidget screenName="SalesDashboardScreen" visible={showAIChat} onClose={() => setShowAIChat(false)} title="Sales AI" />

    </SafeAreaView>
  );
}

// ==================== STYLES ====================
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F3F4F6' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F3F4F6' },
  loadingText: { fontSize: 16, color: '#6B7280', marginTop: 16 },
  loadingSubtext: { fontSize: 14, color: '#9CA3AF', marginTop: 4 },
  contentContainer: { flex: 1 },

  // Error
  errorBanner: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FEF2F2', padding: 8, paddingHorizontal: 16 },
  errorText: { flex: 1, color: '#991B1B', fontSize: 12, marginLeft: 8 },
  retryText: { color: '#3B82F6', fontWeight: '600', fontSize: 12 },

  // Empty state
  emptyState: { alignItems: 'center', padding: 32 },
  emptyText: { fontSize: 16, color: '#6B7280', marginTop: 12 },
  emptySubtext: { fontSize: 13, color: '#9CA3AF', marginTop: 4 },

  // Header
  header: { paddingTop: 8, paddingBottom: 16, paddingHorizontal: 16 },
  headerContent: { flexDirection: 'row', alignItems: 'center' },
  backButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.1)', justifyContent: 'center', alignItems: 'center' },
  headerCenter: { flex: 1, marginLeft: 12 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#FFFFFF' },
  headerSubtitle: { fontSize: 13, color: '#9CA3AF', marginTop: 2 },
  headerButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.1)', justifyContent: 'center', alignItems: 'center' },

  companyBar: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 10, gap: 6 },
  companyChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 12, paddingHorizontal: 8, paddingVertical: 4 },
  companyChipText: { color: '#9CA3AF', fontSize: 11, marginLeft: 4 },

  // Tabs
  tabsContainer: { backgroundColor: '#FFFFFF', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  tabsContent: { paddingHorizontal: 12, paddingVertical: 8 },
  tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 8, marginRight: 4, borderRadius: 20 },
  activeTab: { backgroundColor: '#EFF6FF' },
  tabText: { fontSize: 13, color: '#6B7280', marginLeft: 6, fontWeight: '500' },
  activeTabText: { color: '#3B82F6', fontWeight: '600' },

  // Period
  periodContainer: { backgroundColor: '#FFFFFF', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  periodContent: { paddingHorizontal: 16 },
  periodButton: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, marginRight: 8, backgroundColor: '#F3F4F6' },
  activePeriodButton: { backgroundColor: '#3B82F6' },
  periodButtonText: { fontSize: 13, color: '#6B7280', fontWeight: '500' },
  activePeriodButtonText: { color: '#FFFFFF' },

  // Metrics
  metricsContainer: { padding: 16 },
  row: { justifyContent: 'space-between' },
  metricCard: { backgroundColor: '#FFFFFF', borderRadius: 12, padding: 16, width: (width - 48) / 2, marginBottom: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  metricHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  metricIcon: { width: 44, height: 44, borderRadius: 22, justifyContent: 'center', alignItems: 'center' },
  metricTrend: { flexDirection: 'row', alignItems: 'center' },
  metricTrendText: { fontSize: 12, fontWeight: '600', marginLeft: 4 },
  metricValue: { fontSize: 20, fontWeight: 'bold', color: '#1F2937', marginBottom: 4 },
  metricTitle: { fontSize: 12, color: '#6B7280' },

  // Sections
  sectionTitle: { fontSize: 18, fontWeight: 'bold', color: '#1F2937', marginBottom: 16 },
  sectionHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  sectionSubtitle: { fontSize: 13, color: '#9CA3AF' },

  // Target
  targetContainer: { backgroundColor: '#FFFFFF', margin: 16, padding: 20, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  targetItem: { marginBottom: 20 },
  targetHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  targetPeriod: { fontSize: 14, fontWeight: '600', color: '#1F2937' },
  targetPercentage: { fontSize: 14, fontWeight: 'bold', color: '#3B82F6' },
  targetProgress: { marginBottom: 8 },
  progressBar: { height: 8, backgroundColor: '#F3F4F6', borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4 },
  targetDetails: { flexDirection: 'row', justifyContent: 'space-between' },
  targetText: { fontSize: 12, color: '#6B7280' },
  targetRemaining: { fontSize: 12, color: '#6B7280' },

  // Chart
  chartContainer: { backgroundColor: '#FFFFFF', margin: 16, padding: 20, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  chartContent: { flexDirection: 'row', alignItems: 'flex-end', paddingVertical: 16, height: 200 },
  chartBar: { alignItems: 'center', marginRight: 12, width: 40 },
  barContainer: { width: 24, alignItems: 'center', justifyContent: 'flex-end', height: 140 },
  targetBar: { width: 24, backgroundColor: '#E5E7EB', borderRadius: 4, position: 'absolute', bottom: 0 },
  revenueBar: { width: 16, borderRadius: 4, zIndex: 1 },
  chartLabel: { fontSize: 10, color: '#6B7280', marginTop: 4 },
  chartValue: { fontSize: 9, color: '#9CA3AF', marginTop: 2 },
  chartLegend: { flexDirection: 'row', justifyContent: 'center', marginTop: 12 },
  legendItem: { flexDirection: 'row', alignItems: 'center', marginHorizontal: 12 },
  legendColor: { width: 12, height: 12, borderRadius: 3, marginRight: 6 },
  legendText: { fontSize: 12, color: '#6B7280' },

  // Pipeline
  pipelineContainer: { backgroundColor: '#FFFFFF', margin: 16, padding: 20, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  stageCard: { backgroundColor: '#F9FAFB', borderRadius: 8, padding: 16, marginBottom: 12 },
  stageHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  stageInfo: { flexDirection: 'row', alignItems: 'center' },
  stageIndicator: { width: 12, height: 12, borderRadius: 6, marginRight: 8 },
  stageName: { fontSize: 16, fontWeight: '600', color: '#1F2937' },
  stageValue: { fontSize: 16, fontWeight: 'bold', color: '#10B981' },
  stageMetrics: { flexDirection: 'row', justifyContent: 'space-between' },
  stageMetric: { alignItems: 'center' },
  stageMetricLabel: { fontSize: 12, color: '#6B7280', marginBottom: 4 },
  stageMetricValue: { fontSize: 14, fontWeight: '600', color: '#1F2937' },

  // Performers
  performersContainer: { backgroundColor: '#FFFFFF', margin: 16, padding: 20, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  performerCard: { backgroundColor: '#F9FAFB', borderRadius: 8, padding: 16, marginBottom: 12 },
  performerHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  performerInfo: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  performerAvatar: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#3B82F6', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  performerAvatarText: { fontSize: 14, fontWeight: 'bold', color: '#FFFFFF' },
  performerDetails: { flex: 1 },
  performerName: { fontSize: 16, fontWeight: '600', color: '#1F2937' },
  performerRank: { fontSize: 12, color: '#6B7280', marginTop: 2 },
  performerTrend: { flexDirection: 'row', alignItems: 'center' },
  performerTrendText: { fontSize: 12, fontWeight: '600', marginLeft: 4 },
  performerMetrics: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
  performerMetric: { alignItems: 'center' },
  performerMetricLabel: { fontSize: 12, color: '#6B7280', marginBottom: 4 },
  performerMetricValue: { fontSize: 14, fontWeight: '600', color: '#1F2937' },
  performerProgress: { marginTop: 8 },
  performerProgressHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  performerProgressLabel: { fontSize: 12, color: '#6B7280' },
  performerProgressValue: { fontSize: 12, fontWeight: '600', color: '#3B82F6' },

  // Activities
  activitiesContainer: { backgroundColor: '#FFFFFF', margin: 16, padding: 20, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  activityCard: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  activityIcon: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#F3F4F6', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  activityContent: { flex: 1 },
  activityTitle: { fontSize: 14, fontWeight: '600', color: '#1F2937', marginBottom: 4 },
  activityDescription: { fontSize: 12, color: '#6B7280', marginBottom: 6 },
  activityFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  activityUser: { fontSize: 12, color: '#3B82F6', fontWeight: '500' },
  activityTime: { fontSize: 12, color: '#9CA3AF' },
  activityValue: { fontSize: 12, fontWeight: '600', color: '#10B981' },

  // Insights
  insightsContainer: { backgroundColor: '#FFFFFF', margin: 16, padding: 20, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  insightCard: { backgroundColor: '#F9FAFB', borderRadius: 8, padding: 16, marginBottom: 12 },
  insightHeader: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
  insightIcon: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#FFFFFF', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  insightInfo: { flex: 1 },
  insightTitle: { fontSize: 16, fontWeight: '600', color: '#1F2937', marginBottom: 4 },
  impactBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12, alignSelf: 'flex-start' },
  impactBadgeText: { fontSize: 10, fontWeight: '600', color: '#FFFFFF' },
  insightDescription: { fontSize: 14, color: '#6B7280', lineHeight: 20, marginBottom: 12 },
  insightAction: { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: '#FFFBEB', padding: 12, borderRadius: 8 },
  insightActionText: { flex: 1, fontSize: 14, color: '#92400E', marginLeft: 8, lineHeight: 20 },

  // Modal
  modalContainer: { flex: 1, backgroundColor: '#FFFFFF' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  modalTitle: { fontSize: 18, fontWeight: 'bold', color: '#1F2937', flex: 1 },
  modalCloseButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#F3F4F6', justifyContent: 'center', alignItems: 'center' },
  modalContent: { flex: 1, padding: 16 },
  modalSection: { marginBottom: 24 },
  modalSectionTitle: { fontSize: 16, fontWeight: '600', color: '#1F2937', marginBottom: 8 },
  modalText: { fontSize: 14, color: '#6B7280', lineHeight: 20 },
});